

const QUERY_SPLIT_LIMIT = 2;
/** Only a choice, never a price, entitlement, return URL or contract acceptance. */
export interface EntryIntent { planId: string; billingCycle: "monthly" | "yearly"; createdAt: number; owner?: string }
const key = "mqorva:entry-intent:v1";
const lifetime = 7 * 24 * 60 * 60 * 1000;
export function parseEntryIntent(search: string, now = Date.now()): EntryIntent | null {
  const params = new URLSearchParams(search);
  const planId = params.get("plan");
  const cycle = params.get("cycle") ?? "monthly";
  if (!planId || !/^[a-zA-Z0-9_-]{1,80}$/.test(planId) || !["monthly", "yearly"].includes(cycle)) return null;
  return { planId, billingCycle: cycle as EntryIntent["billingCycle"], createdAt: now };
}
export function saveEntryIntent(intent: EntryIntent) {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(key, JSON.stringify(intent)); } catch { /* Choice remains in the entry URL. */ }
}
export function captureEntryIntent() {
  if (typeof window === "undefined") return;
  const hashQuery = window.location.hash.split("?", QUERY_SPLIT_LIMIT)[1] ?? "";
  const intent = parseEntryIntent(hashQuery) ?? parseEntryIntent(window.location.search);
  if (intent) {
    saveEntryIntent(intent);
  } else if (new URLSearchParams(hashQuery).has("plan") || new URLSearchParams(window.location.search).has("plan")) {
    clearEntryIntent();
  }
}
export function readEntryIntent(owner?: string): EntryIntent | null {
  if (typeof window === "undefined") return null;
  try {
    const value: unknown = JSON.parse(window.localStorage.getItem(key) ?? "null");
    if (!value || typeof value !== "object") return null;
    const candidate = value as EntryIntent;
    if (typeof candidate.planId !== "string" || !parseEntryIntent(`plan=${encodeURIComponent(candidate.planId)}&cycle=${candidate.billingCycle}`)
      || typeof candidate.createdAt !== "number" || candidate.createdAt > Date.now()
      || Date.now() - candidate.createdAt > lifetime || (candidate.owner && candidate.owner !== owner)) return null;
    return candidate;
  } catch { return null; }
}
export function claimEntryIntent(owner: string): EntryIntent | null {
  const intent = readEntryIntent(owner);
  if (intent) {
    try { window.localStorage.setItem(key, JSON.stringify({ ...intent, owner })); } catch { /* Persistence is optional. */ }
  }
  return intent;
}
export function clearEntryIntent() {
  if (typeof window === "undefined") return;
  try { window.localStorage.removeItem(key); } catch { /* Storage may be unavailable. */ }
}
